import haptic from '@/common/utils/haptics';
import { useGetUserActiveTurnsCount } from '@/common/apis/services/booking/getUserActiveTurnsCount';
import { useChatsUnreadCount } from '@/common/apis/services/hami/chatsUnreadCount';
import { useDoctorDeviceCached } from '@/common/hooks/useDoctorDeviceCached';
import { hasDoctorIdentity } from '@/common/utils/doctorIdentity';
import useApplication from '@/common/hooks/useApplication';
import useIsNewAppShell from '@/common/hooks/useIsNewAppShell';
import useCustomize from '@/common/hooks/useCustomize';
import useResponsive from '@/common/hooks/useResponsive';
import classNames from '@/common/utils/classNames';
import { useLoginModalContext } from '@/modules/login/context/loginModal';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { useSearchStore } from '@/modules/search/store/search';
import { useRouter } from 'next/dist/client/router';
import { useEffect, useRef } from 'react';
import Avatar from '../../atom/avatar';
import Text from '../../atom/text/text';
import CalenderIcon from '../../icons/calender';
import ChatIcon from '../../icons/chat';
import HomeIcon from '../../icons/home';
import SearchIcon from '../../icons/search';
import UserCircle from '../../icons/userCircle';
import { NavProfileIcon, NavSearchIcon } from './navIcons';

const unreadBadgeLabel = (count: number) => (count > 99 ? '+۹۹' : String(count));

const ChatIconWithBadge = ({ count, isSolid }: { count: number; isSolid?: boolean }) => (
  <div className="relative">
    {count > 0 && (
      <span
        aria-label={`${count} پیام خوانده‌نشده`}
        className="absolute -top-1 -left-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium leading-none text-white"
      >
        {unreadBadgeLabel(count)}
      </span>
    )}
    <ChatIcon isSolid={isSolid} />
  </div>
);

// Eased (ease-in-out) white fade, max 90% opacity.
const FLOATING_NAV_FADE = `linear-gradient(to top, rgb(255 255 255 / 0.9) 0%, rgb(255 255 255 / 0.888) 8.1%, rgb(255 255 255 / 0.856) 15.5%, rgb(255 255 255 / 0.806) 22.5%, rgb(255 255 255 / 0.742) 29%, rgb(255 255 255 / 0.667) 35.3%, rgb(255 255 255 / 0.583) 41.2%, rgb(255 255 255 / 0.495) 47.1%, rgb(255 255 255 / 0.405) 52.9%, rgb(255 255 255 / 0.317) 58.8%, rgb(255 255 255 / 0.233) 64.7%, rgb(255 255 255 / 0.158) 71%, rgb(255 255 255 / 0.094) 77.5%, rgb(255 255 255 / 0.044) 84.5%, rgb(255 255 255 / 0.012) 91.9%, rgb(255 255 255 / 0.0) 100%)`;

const pillTransform = (index: number) => `translateX(calc(${-Math.max(index, 0)} * (100% + 2px)))`;

export const BottomNavigation = () => {
  const router = useRouter();
  const user = useUserInfoStore(state => state.info);
  const isLogin = useUserInfoStore(state => state.isLogin);
  const { handleOpenLoginModal } = useLoginModalContext();
  const turnsCount = useUserInfoStore(state => state.turnsCount);
  const setTurnsCount = useUserInfoStore(state => state.setTurnsCount);
  const getUserActiveTurnsCount = useGetUserActiveTurnsCount();
  const city = useSearchStore(state => state.city);
  const isApplication = useApplication();
  // New Android app (≥ 9100): floating pill bar like Telegram's (owner, 2026-10-13).
  const isAppShell = useIsNewAppShell();
  const customize = useCustomize(state => state.customize);
  const deviceCached = useDoctorDeviceCached();
  const isDoctor = hasDoctorIdentity(user, deviceCached);

  const { isMobile } = useResponsive();
  // The nav is `md:hidden`: desktop visitors never see the badge, so they never ask Hami for it.
  const chatsUnread = useChatsUnreadCount(isLogin && isMobile);
  const wasInChatsRef = useRef(false);

  const isShowDashboard = !customize.partnerKey;

  const isDoctorShell = isDoctor && !customize.partnerKey;
  // Doctors get the floating pill on the mobile site too (owner, 2026-10-26); everyone else only in the app.
  const isFloating = isAppShell || isDoctorShell;

  // One icon family (outline, solid when active); the new app uses the same set with a lighter pill.
  const icons = {
    home: { icon: <HomeIcon />, fillIcon: <HomeIcon isSolid /> },
    calendar: { icon: <CalenderIcon />, fillIcon: <CalenderIcon isSolid /> },
    search: { icon: <NavSearchIcon />, fillIcon: <NavSearchIcon isSolid /> },
    chat: {
      icon: <ChatIconWithBadge count={chatsUnread.count} />,
      fillIcon: <ChatIconWithBadge count={chatsUnread.count} isSolid />,
    },
    profile: { icon: <NavProfileIcon />, fillIcon: <NavProfileIcon isSolid /> },
  };

  const servicesMenu = {
    name: 'پروفایل',
    ...icons.profile,
    link: '/patient',
    pattern: '/patient',
    privateRoute: false,
    exact: false,
  };

  useEffect(() => {
    isLogin && handleGetTurnsCount();
    // Reading chats is what clears the badge: refresh right after leaving them, not on every navigation.
    const inChats = router.pathname.startsWith('/_/chats');
    if (wasInChatsRef.current && !inChats && isLogin && isMobile) chatsUnread.refetch();
    wasInChatsRef.current = inChats;
  }, [router.asPath, isLogin]);

  const handleGetTurnsCount = async () => {
    const { data } = await getUserActiveTurnsCount.mutateAsync();
    setTurnsCount({
      ...data.result,
    });
  };

  const doctorFullName = `${user.name ?? ''} ${user.family ?? ''}`.trim();
  const doctorMenu = [
    {
      name: 'خانه',
      ...icons.home,
      link: '/_/',
      pattern: '/_',
      privateRoute: true,
      exact: false,
    },
    {
      name: 'مراجعین من',
      ...icons.calendar,
      link: '/dashboard/apps/drapp/appointments/',
      exact: true,
      pattern: '',
      privateRoute: true,
    },
    {
      name: 'جستجو',
      ...icons.search,
      link: `/s${city.en_slug !== 'ir' ? `/${city.en_slug}` : ''}`,
      pattern: '/s/[[...params]]',
      privateRoute: false,
      exact: false,
    },
    {
      name: 'گفت‌وگو‌ها',
      ...icons.chat,
      link: `/_/chats`,
      pattern: ['/_/chats', '/_/chats/[[...hami]]'],
      privateRoute: true,
      exact: false,
    },
    // Telegram-style profile tab with the doctor's own round photo (the app and the mobile site).
    {
      name: 'پروفایل',
      icon: <Avatar src={user.image ?? ''} name={doctorFullName} width={24} height={24} className="text-[8px]" />,
      fillIcon: (
        <Avatar
          src={user.image ?? ''}
          name={doctorFullName}
          width={24}
          height={24}
          className="text-[8px] ring-2 ring-primary ring-offset-1"
        />
      ),
      link: '/_/profile',
      pattern: '/_/profile',
      privateRoute: true,
      exact: false,
    },
  ];

  const menus = [
    {
      name: 'خانه',
      ...icons.home,
      link: isApplication ? '/apphome' : '/',
      // both homes count as «خانه» (/apphome can also be opened outside the app shell)
      pattern: ['/', '/apphome'],
      privateRoute: false,
      exact: false,
    },
    {
      name: 'جستجو',
      ...icons.search,
      link: `/s${city.en_slug !== 'ir' ? `/${city.en_slug}` : ''}`,
      pattern: '/s/[[...params]]',
      privateRoute: false,
      exact: false,
    },
    {
      name: 'گفت‌وگو‌ها',
      ...icons.chat,
      link: `/_/chats`,
      pattern: ['/_/chats', '/_/chats/[[...hami]]'],
      privateRoute: true,
      exact: false,
    },
    isDoctor
      ? {
          name: 'مراجعین من',
          ...icons.calendar,
          link: '/dashboard/apps/drapp/appointments/',
          exact: true,
          pattern: '',
          privateRoute: true,
        }
      : {
          name: 'نوبت‌های من',
          icon: (
            <div className="relative">
              {!!turnsCount.presence && (
                <div className="absolute flex items-center justify-center w-4 h-4 text-xs text-white bg-red-500 rounded-full -top-1 -left-1">
                  {turnsCount.presence}
                </div>
              )}
              {icons.calendar.icon}
            </div>
          ),
          fillIcon: icons.calendar.fillIcon,
          link: isShowDashboard ? '/dashboard/appointments/' : '/patient/appointments',
          ...(isShowDashboard && { exact: true }),
          pattern: '/patient/appointments',
          privateRoute: true,
          exact: false,
        },
    servicesMenu,
  ];

  const handleChangeRoute = (link: string, privateRoute: boolean) => {
    if (!isLogin && privateRoute) {
      handleOpenLoginModal({
        state: true,
        postLogin: () => router.push(link),
      });
      return;
    }
    router.push(link);
  };

  const navItems = isDoctorShell ? doctorMenu : menus;

  const isItemActive = ({ link, pattern, exact }: (typeof navItems)[number]) =>
    exact
      ? router.asPath === link
      : router.pathname === pattern || (Array.isArray(pattern) && pattern.includes(router.pathname));
  const activeIndex = navItems.findIndex(item => isItemActive(item));

  const renderNavItem = (
    item: (typeof navItems)[number],
    key: string,
    opts?: { flex?: boolean },
  ) => {
    const { icon, fillIcon, name, link, privateRoute } = item;
    const active = isItemActive(item);

    return (
      <div
        key={key}
        role="link"
        tabIndex={0}
        aria-current={active ? 'page' : undefined}
        onClick={() => {
          if (!active) haptic('selection');
          handleChangeRoute(link, privateRoute);
        }}
        onKeyDown={e => e.key === 'Enter' && handleChangeRoute(link, privateRoute)}
        className={classNames(
          'press flex cursor-pointer flex-col items-center justify-center font-medium text-slate-700 transition-all',
          isFloating
            ? 'relative z-10 h-full w-full min-w-0 gap-1 rounded-full duration-200'
            : ['space-y-1 scale-95', opts?.flex ? 'min-w-0 flex-1' : 'w-[70px]'],
          {
            '!text-primary': active,
            'font-bold scale-100': active && !isFloating,
            // The highlight is one sliding pill (below), so a bolder label never resizes it.
            'font-bold': active && isFloating,
          },
        )}
      >
        {/* every glyph in the same 24px box so the labels line up */}
        <span className="flex h-6 w-6 items-center justify-center">{fillIcon && active ? fillIcon : icon}</span>
        <Text fontSize="xs" className={classNames('whitespace-nowrap', { '!text-[clamp(9px,calc(2.5vw_+_1px),10px)] !leading-[14px]': isFloating })}>
          {name}
        </Text>
      </div>
    );
  };

  return (
    <div
      data-bottom-nav={isFloating ? 'floating' : 'docked'}
      // Own view-transition layer: the tab-switch crossfade (routeFade) must not hide the pill's slide.
      style={isAppShell ? ({ viewTransitionName: 'bottom-nav' } as React.CSSProperties) : undefined}
      className={classNames(
        'fixed bottom-0 left-0 z-40 flex items-center justify-between w-full pb-[env(safe-area-inset-bottom)] print:hidden md:hidden',
        // Floating: 58px pill + 12px gap = 4rem + 6px; docked bars follow via html[data-floating-bottom-nav] (utils/newAppShell).
        isFloating
          ? 'h-[calc(4rem_+_6px_+_env(safe-area-inset-bottom))] px-[clamp(12px,calc(20vw_-_52px),24px)] pointer-events-none'
          : 'h-[calc(4rem_+_env(safe-area-inset-bottom))] px-4 bg-[#F9F9F9] border-t border-[#d5d8db]',
      )}
    >
      {isFloating && (
        // Soft fade over the content behind the pill; eased stops so its top edge never shows.
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-[calc(100%_+_24px)]"
          style={{ background: FLOATING_NAV_FADE }}
        />
      )}
      {isFloating ? (
        <div
          className="pointer-events-auto relative mb-3 grid h-[58px] w-full gap-0.5 rounded-full border border-slate-100 bg-white p-1 shadow-[0_4px_16px_rgb(0_0_0_/_0.10),0_1px_3px_rgb(0_0_0_/_0.06)]"
          style={{ gridTemplateColumns: `repeat(${navItems.length}, minmax(0, 1fr))` }}
        >
          {/* Telegram-style selection: one pill slides between the equal-width cells (RTL: toward the left).
              The new app renders a single persistent nav (floatingBottomNavigation), so this transition always runs. */}
          <span
            aria-hidden
            className="absolute bottom-1 right-1 top-1 rounded-full bg-primary/[0.06] transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none"
            style={{
              width: `calc((100% - 8px - ${(navItems.length - 1) * 2}px) / ${navItems.length})`,
              transform: pillTransform(activeIndex),
              opacity: activeIndex < 0 ? 0 : 1,
            }}
          />
          {navItems.map((item, index) => renderNavItem(item, `${item.name}-${index}`))}
        </div>
      ) : (
        navItems.map((item, index) => renderNavItem(item, `${item.name}-${index}`))
      )}
    </div>
  );
};

export default BottomNavigation;
